import {
  Drawer,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import commons from 'legacyfrontend/components/commons';
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { selectTemplateData } from 'legacyfrontend/redux/selectors/application-selectors';
import SectionMenuIcon from 'frontend/shared/SectionMenuIcon';

const NavBarItem = ({ item, url, onClick, color }) => (
  <ListItem
    component='a'
    href={url}
    target={item.interna ? '_self' : '_blank'}
    onClick={onClick}
    sx={{
      py: 1.5,
      px: 2,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    }}
  >
    <ListItemIcon sx={{ minWidth: '40px' }}>
      <SectionMenuIcon
        codigo={item.icono?.codigo}
        descripcion={item.icono?.descripcion}
        color={color}
      />
    </ListItemIcon>
    <ListItemText
      primary={item.descripcion}
      primaryTypographyProps={{
        sx: { fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' },
      }}
    />
  </ListItem>
);

const renderNavBarComponents = (navBarItems, closeDrawer) => {
  const { background } = useColors();
  return (
    navBarItems &&
    navBarItems.map((item) => {
      const url = commons.urlForNavBarItem(item);
      return (
        <NavBarItem key={item.id} item={item} url={url} onClick={closeDrawer} color={background} />
      );
    })
  );
};

const NavBarMobile = ({ isScrolled = false }) => {
  const [open, setOpen] = useState(false);
  const { background } = useColors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const templateData = useSelector(selectTemplateData);

  const navBarItems = useMemo(
    () => (templateData?.mainMenu || []).filter((item) => item.activo === true),
    [templateData?.mainMenu]
  );

  const email = useMemo(
    () => templateData?.header?.email_contacto,
    [templateData?.header?.email_contacto]
  );
  const phoneNumber = useMemo(
    () => templateData?.header?.telefono_contacto,
    [templateData?.header?.telefono_contacto]
  );

  const showContactPanel = useMemo(
    () => (email && email !== '') || (phoneNumber && phoneNumber !== ''),
    [email, phoneNumber]
  );

  const handleToggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const handleCloseDrawer = () => {
    setOpen(false);
  };

  const drawerAnchor = isMobile ? 'bottom' : 'right';

  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? 'auto' : 320,
        height: isMobile ? 'auto' : '100%',
        maxHeight: isMobile ? '70vh' : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header con botón de cerrar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ fontSize: '1.125rem', fontWeight: 600, color: 'text.primary' }}>Menú</Box>
        <IconButton onClick={handleCloseDrawer} size='small' sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Lista de navegación */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ py: 0 }}>{renderNavBarComponents(navBarItems, handleCloseDrawer)}</List>
      </Box>

      {/* Panel de contacto */}
      {showContactPanel && (
        <Box
          sx={{
            p: 2,
            backgroundColor: 'grey.800',
            color: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {email && email !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Box sx={{ fontSize: '0.875rem' }}>{email}</Box>
            </Box>
          )}
          {phoneNumber && phoneNumber !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Box sx={{ fontSize: '0.875rem' }}>{phoneNumber}</Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <button
        type='button'
        aria-label='Abrir menú'
        onClick={handleToggleDrawer}
        className={`main-header-menu-button flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg border-0 p-0 transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${isScrolled ? 'bg-white hover:bg-gray-50' : 'bg-white/90 hover:bg-white backdrop-blur-sm drop-shadow-lg'}`}
        style={{ color: background }}
      >
        <MenuIcon fontSize='large' />
      </button>

      <Drawer
        anchor={drawerAnchor}
        open={open}
        onClose={handleCloseDrawer}
        disableScrollLock={false}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: false,
        }}
        PaperProps={{
          sx: {
            ...(isMobile && {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }),
            ...(!isMobile && {
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default NavBarMobile;
